"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { useTranslations } from "next-intl"

import { cn } from "@/lib/utils"
import { Icon } from "@/components/ui/icon"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // A calm, slightly-warm scrim — deep enough to defocus the page without
      // crushing it. Soft blur keeps the surface behind legible as context.
      "fixed inset-0 z-[var(--z-modal-overlay)] bg-scrim/40 backdrop-blur-[3px] duration-[var(--duration-medium-2)] ease-[var(--ease-emphasized)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:duration-0 motion-reduce:animate-none",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogContentSize = "sm" | "md" | "lg" | "xl"

// Width tokens for the dialog surface, aligned to the spec Modal size scale
// (sm 380 / md 480 / lg 720 / xl 920) for "compact prompt", "form / detail"
// and "wide editor" modals respectively.
const DIALOG_SIZE_CLASS: Record<DialogContentSize, string> = {
  sm: "sm:max-w-[380px]",
  md: "sm:max-w-[480px]",
  lg: "sm:max-w-[720px]",
  xl: "sm:max-w-[920px]",
}

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  size?: DialogContentSize
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, size = "md", ...props }, ref) => {
  const translate = useTranslations()

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          // Surface: an extra-large rounded card lifted on the dramatic elevation
          // ramp (--elev-5) so it reads as the top layer. The hairline outline
          // sharpens the edge against the blurred scrim. Body padding lives on
          // the inner scroll region, not here, so header/footer can span edge to
          // edge while content keeps its breathing room.
          "fixed left-[50%] top-[50%] z-[var(--z-modal)] flex w-full max-h-[88vh] flex-col translate-x-[-50%] translate-y-[-50%] overflow-hidden rounded-[var(--shape-corner-extra-large)] border border-outline-variant/70 bg-background text-popover-foreground shadow-[var(--elev-5)]",
          // Entrance: a confident decelerated rise + subtle scale-up; exit is a
          // quick accelerated settle. Both gated for reduced motion.
          "duration-[var(--duration-medium-2)] data-[state=open]:ease-[var(--ease-emphasized-decel)] data-[state=closed]:ease-[var(--ease-emphasized-accel)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-[0.97] data-[state=open]:zoom-in-[0.97] data-[state=open]:slide-in-from-bottom-2 data-[state=closed]:slide-out-to-bottom-1 motion-reduce:duration-0 motion-reduce:animate-none motion-reduce:data-[state=open]:slide-in-from-bottom-0",
          DIALOG_SIZE_CLASS[size],
          className
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-3.5 top-3.5 z-[1] inline-flex size-9 items-center justify-center rounded-full text-on-surface-variant hover:bg-foreground/8 hover:text-foreground active:bg-foreground/12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors duration-[var(--duration-short-3)] ease-[var(--ease-emphasized)] motion-reduce:transition-none disabled:pointer-events-none">
          <Icon icon="lucide:x" className="size-5" />
          <span className="sr-only">{translate('common.actions.close')}</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    // Header occupies its own band with generous side padding; leaves room on
    // the right for the absolute close affordance so titles never collide.
    className={cn("flex flex-col gap-1.5 px-6 pb-4 pt-5 pr-14", className)}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

type DialogFooterProps = React.HTMLAttributes<HTMLDivElement> & {
  /**
   * Opt-in sticky footer variant for long-form modals.
   * Pins the footer to the bottom with a tonal separator so primary CTA + Cancel
   * stay in view while the body scrolls.
   */
  sticky?: boolean
}

const DialogFooter = ({
  className,
  sticky = false,
  ...props
}: DialogFooterProps) => (
  <div
    className={cn(
      // Right-aligned action band sitting on a faint tonal shelf — visually
      // anchored to the surface bottom, separated from the body by a hairline.
      "flex flex-col-reverse gap-2 border-t border-outline-variant/60 bg-surface-1/50 px-6 py-4 sm:flex-row sm:items-center sm:justify-end",
      sticky === true &&
        "sticky bottom-0 z-[1] rounded-b-[var(--shape-corner-extra-large)] bg-background/95 backdrop-blur-sm supports-[backdrop-filter]:bg-background/80",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      // Canonical modal title: MD3 title-large, tight tracking for a crafted feel.
      "type-title-l tracking-[-0.01em] text-foreground",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("type-body-m text-on-surface-variant", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
